import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { Check, Loader2, PenLine, Trash2, Upload } from "lucide-react";

import { BooleanCheckboxIcon } from "@/components/BooleanCheckboxIcon";

import {
  MediaCarouselOverlay,
  type MediaCarouselState,
} from "@/components/MediaCarouselOverlay";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { HtmlFieldEditor } from "@/components/HtmlFieldEditor";
import { MarkdownFieldEditor } from "@/components/MarkdownFieldEditor";
import { SelectFieldEditor } from "@/components/SelectFieldEditor";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAppContext, type DecryptedBlock } from "@/context/AppContext";
import { encrypt } from "@/utls/CryptoHelpers";
import { useBreadcrumbs } from "@/context/BreadcrumbContext";
import {
  type MediaItem,
  mediaItemSrc,
  mediaPreviewLooksImage,
  mediaPreviewLooksVideo,
  normalizeRawMediaList,
  reindexMediaItems,
} from "@/lib/mediaListHelpers";
import {
  ensureRowDataRecord,
  getGridCell,
  getTablePayload,
  isHtmlSchemaType,
  isMarkdownSchemaType,
  type GridCell,
  resolveEditSchemaFields,
  isRecord,
  normalizeToCell,
  packFormRows,
  parseBoolean,
  rowBelongsToTable,
  type SchemaField,
} from "@/lib/tableBlockHelpers";
import { cn } from "@/lib/utils";

type FieldDraft = { value: unknown; data?: unknown; mediaJson?: string };

function parseTagsForSelect(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.map((t) => String(t).trim()).filter(Boolean);
  }
  if (raw != null && raw !== "") {
    return [String(raw).trim()].filter(Boolean);
  }
  return [];
}

/** Matches `textarea`, `text area`, `text_area`, etc. */
function isTextareaSchemaType(type: string): boolean {
  return type.replace(/\s+/g, "").replace(/_/g, "").toLowerCase() === "textarea";
}

function safeJsonParseArray(text: string): unknown[] | null {
  try {
    const v = JSON.parse(text) as unknown;
    return Array.isArray(v) ? v : null;
  } catch {
    return null;
  }
}

function mediaItemsFromDraft(draft: FieldDraft): MediaItem[] {
  if (draft.mediaJson !== undefined) {
    const parsed = safeJsonParseArray(draft.mediaJson);
    if (parsed) return normalizeRawMediaList(parsed);
  }
  return Array.isArray(draft.data)
    ? normalizeRawMediaList(draft.data as unknown[])
    : [];
}

function draftWithMediaItems(draft: FieldDraft, items: MediaItem[]): FieldDraft {
  return { ...draft, mediaJson: JSON.stringify(items), data: items };
}

/** `insertBeforeGap` is gap index before cell `insertBeforeGap` in the original layout (0…items.length). */
function moveMediaItem({
  items,
  from,
  insertBeforeGap,
}: {
  items: MediaItem[];
  from: number;
  insertBeforeGap: number;
}): MediaItem[] {
  if (from < 0 || from >= items.length) return items;
  const next = [...items];
  const [removed] = next.splice(from, 1);
  let to = insertBeforeGap;
  if (insertBeforeGap > from) to -= 1;
  to = Math.max(0, Math.min(to, next.length));
  next.splice(to, 0, removed);
  return reindexMediaItems(next);
}

function draftFromCell(field: SchemaField, cell: GridCell | null | undefined): FieldDraft {
  if (field.type === "mediaList") {
    const data = cell?.data;
    const json =
      data === undefined ? "[]" : JSON.stringify(data, null, 2);
    return { value: cell?.value, data: cell?.data, mediaJson: json };
  }
  return { value: cell?.value, data: cell?.data };
}

function packStoredCell({
  field,
  draft,
}: {
  field: SchemaField;
  draft: FieldDraft;
}): unknown {
  if (field.type === "mediaList") {
    const parsed =
      draft.mediaJson !== undefined
        ? safeJsonParseArray(draft.mediaJson)
        : null;
    const rawArr =
      parsed !== null
        ? parsed
        : Array.isArray(draft.data)
          ? draft.data
          : [];
    const data = normalizeRawMediaList(rawArr);
    return { value: draft.value, data };
  }
  if (field.type === "checkbox") {
    return normalizeToCell(parseBoolean(draft.value));
  }
  if (field.type === "select") {
    const raw = draft.value;
    const tags =
      typeof raw === "string"
        ? raw.split("\n").map((t) => t.trim()).filter(Boolean)
        : parseTagsForSelect(raw);
    return normalizeToCell(tags.length <= 1 ? tags[0] ?? "" : tags);
  }
  if (draft.data !== undefined) {
    return { value: draft.value, data: draft.data };
  }
  return normalizeToCell(draft.value);
}

type BlockHistoryStacks = {
  past: DecryptedBlock[];
  future: DecryptedBlock[];
};

function cloneBlock(block: DecryptedBlock): DecryptedBlock {
  return {
    id: block.id,
    type: block.type,
    data: structuredClone(block.data),
  };
}

function draftDataMatchesPersisted({
  rowBlock,
  rowValid,
  schema,
  drafts,
}: {
  rowBlock: DecryptedBlock;
  rowValid: boolean;
  schema: SchemaField[];
  drafts: Record<string, FieldDraft>;
}): boolean {
  if (!rowValid) return true;
  const nextBlock: DecryptedBlock = {
    id: rowBlock.id,
    type: rowBlock.type,
    data: structuredClone(rowBlock.data),
  };
  const rowRecord = ensureRowDataRecord(nextBlock);
  if (!rowRecord) return true;
  for (const field of schema) {
    const d = drafts[field.key];
    if (!d) continue;
    rowRecord[field.key] = packStoredCell({ field, draft: d });
  }
  return JSON.stringify(rowBlock.data) === JSON.stringify(nextBlock.data);
}

export function EditBlockRoute() {
  const { tableId, blockId } = useParams<{ tableId: string; blockId: string }>();
  const { blocks, isLoading, updateDecryptedBlock } = useAppContext();
  const { setBreadcrumbSegments } = useBreadcrumbs();

  const tableModel = useMemo(() => {
    if (!tableId) return null;
    const tableBlock = blocks.find((b) => b.id === tableId && b.type === "table");
    if (!tableBlock) return null;
    return getTablePayload(tableBlock.data);
  }, [blocks, tableId]);

  const tableName = tableModel?.name ?? "";
  const viewedIsDefault = tableModel?.isDefault === true;
  const schema = tableModel?.schema ?? [];

  const editSchema = useMemo(
    () =>
      resolveEditSchemaFields({ view: tableModel?.defaultView, schema }),
    [tableModel?.defaultView, schema],
  );

  const rowBlock = useMemo(() => {
    if (!blockId) return undefined;
    return blocks.find((b) => b.id === blockId);
  }, [blocks, blockId]);

  const rowValid = useMemo(() => {
    if (!tableId || !rowBlock || rowBlock.type === "table") return false;
    return rowBelongsToTable({ rowBlock, tableId, viewedIsDefault });
  }, [rowBlock, tableId, viewedIsDefault]);

  const [drafts, setDrafts] = useState<Record<string, FieldDraft>>({});
  const [isPersisting, setIsPersisting] = useState(false);
  const [historyVersion, setHistoryVersion] = useState(0);
  const historyRef = useRef<BlockHistoryStacks>({ past: [], future: [] });

  const schemaDraftShape = useMemo(
    () => schema.map((field) => `${field.key}:${field.type}`).join(","),
    [schema],
  );

  useEffect(() => {
    historyRef.current = { past: [], future: [] };
    setHistoryVersion((v) => v + 1);
  }, [blockId]);

  const bumpHistory = useCallback(() => {
    setHistoryVersion((v) => v + 1);
  }, []);

  useEffect(() => {
    if (!rowBlock || !rowValid) return;
    const next: Record<string, FieldDraft> = {};
    for (const field of schema) {
      const cell = getGridCell(rowBlock, field.key);
      next[field.key] = draftFromCell(field, cell);
    }
    setDrafts(next);
  }, [rowBlock, rowValid, schemaDraftShape]);

  const nameCrumb = isLoading ? "…" : tableName || "Table";

  useEffect(() => {
    if (!tableId) {
      setBreadcrumbSegments([]);
      return () => setBreadcrumbSegments([]);
    }
    setBreadcrumbSegments([
      { label: "Tables", to: "/tables" },
      { label: nameCrumb, to: `/tables/${tableId}` },
      { label: "Edit block" },
    ]);
    return () => setBreadcrumbSegments([]);
  }, [nameCrumb, setBreadcrumbSegments, tableId]);

  const persistBlockEncrypted = useCallback(
    async ({ block }: { block: DecryptedBlock }): Promise<boolean> => {
      const secretKey =
        typeof window !== "undefined"
          ? (window.localStorage.accessKey ?? "")
          : "";
      if (!secretKey) {
        console.warn(
          "EditBlockRoute: no accessKey in localStorage; cannot persist",
        );
        return false;
      }
      const encryptedData = await encrypt(
        JSON.stringify(block.data),
        secretKey,
      );
      const res = await fetch(
        `/api/blocks/${encodeURIComponent(block.id)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ encryptedData }),
        },
      );
      if (!res.ok) {
        console.error("EditBlockRoute: persist failed", await res.text());
        return false;
      }
      return true;
    },
    [],
  );

  const ensureSelectFieldOption = useCallback(
    async ({
      fieldKey,
      option,
    }: {
      fieldKey: string;
      option: string;
    }): Promise<boolean> => {
      if (!tableId) return false;
      const trimmed = option.trim();
      if (!trimmed) return false;

      const tableBlock = blocks.find((b) => b.id === tableId && b.type === "table");
      if (!tableBlock) return false;
      const tableData = getTablePayload(tableBlock.data);
      if (!tableData) return false;

      const fieldIndex = tableData.schema.findIndex((f) => f.key === fieldKey);
      if (fieldIndex < 0) return false;
      const field = tableData.schema[fieldIndex];
      if (field?.type !== "select") return false;

      const existing = field.options ?? [];
      if (existing.some((o) => o.toLowerCase() === trimmed.toLowerCase())) {
        return true;
      }

      const nextSchema = tableData.schema.map((f, i) =>
        i === fieldIndex ? { ...f, options: [...existing, trimmed] } : f,
      );
      const rawData = tableBlock.data;
      if (!isRecord(rawData)) return false;
      const nextData = { ...rawData, schema: nextSchema };
      const nextBlock: DecryptedBlock = {
        id: tableBlock.id,
        type: "table",
        data: nextData,
      };
      const ok = await persistBlockEncrypted({ block: nextBlock });
      if (ok) {
        updateDecryptedBlock({ block: nextBlock });
      }
      return ok;
    },
    [blocks, persistBlockEncrypted, tableId, updateDecryptedBlock],
  );

  const pushPastAfterSuccessfulSave = useCallback(
    ({ snapshot }: { snapshot: DecryptedBlock }) => {
      const h = historyRef.current;
      historyRef.current = {
        past: [...h.past, snapshot],
        future: [],
      };
      bumpHistory();
    },
    [bumpHistory],
  );

  const saveRowDrafts = useCallback(
    async (draftOverrides?: Record<string, FieldDraft>) => {
      if (!rowBlock || !rowValid) return;
      const mergedDrafts = draftOverrides
        ? { ...drafts, ...draftOverrides }
        : drafts;
      const nextBlock: DecryptedBlock = {
        id: rowBlock.id,
        type: rowBlock.type,
        data: structuredClone(rowBlock.data),
      };
      const rowRecord = ensureRowDataRecord(nextBlock);
      if (!rowRecord) return;
      for (const field of schema) {
        const d = mergedDrafts[field.key];
        if (!d) continue;
        rowRecord[field.key] = packStoredCell({ field, draft: d });
      }
      if (JSON.stringify(rowBlock.data) === JSON.stringify(nextBlock.data)) {
        return;
      }
      const previousSnapshot = cloneBlock(rowBlock);
      setIsPersisting(true);
      try {
        const ok = await persistBlockEncrypted({ block: nextBlock });
        if (!ok) return;
        pushPastAfterSuccessfulSave({ snapshot: previousSnapshot });
        updateDecryptedBlock({ block: nextBlock });
      } finally {
        setIsPersisting(false);
      }
    },
    [
      drafts,
      persistBlockEncrypted,
      pushPastAfterSuccessfulSave,
      rowBlock,
      rowValid,
      schema,
      updateDecryptedBlock,
    ],
  );

  const saveRowDraftsRef = useRef(saveRowDrafts);
  saveRowDraftsRef.current = saveRowDrafts;

  const blurSaveChainRef = useRef(Promise.resolve());

  const saveRowOnBlur = useCallback(() => {
    blurSaveChainRef.current = blurSaveChainRef.current.then(async () => {
      try {
        await saveRowDraftsRef.current();
      } catch (e) {
        console.error("EditBlockRoute: blur save failed", e);
      }
    });
  }, []);

  const undoRedoFromEditableTarget = (target: EventTarget | null) => {
    const el =
      target instanceof HTMLElement ? target : null;
    if (!el) return false;
    const tag = el.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") {
      return true;
    }
    if (el.isContentEditable) return true;
    return false;
  };

  const undo = useCallback(async () => {
    if (!rowBlock || !rowValid || isPersisting) return;
    const h = historyRef.current;
    if (h.past.length === 0) return;
    const restore = h.past[h.past.length - 1];
    if (restore.id !== rowBlock.id) return;
    const currentSnapshot = cloneBlock(rowBlock);
    historyRef.current = {
      past: h.past.slice(0, -1),
      future: [...h.future, currentSnapshot],
    };
    bumpHistory();
    setIsPersisting(true);
    try {
      const ok = await persistBlockEncrypted({ block: restore });
      if (!ok) {
        historyRef.current = h;
        bumpHistory();
        return;
      }
      updateDecryptedBlock({ block: restore });
    } finally {
      setIsPersisting(false);
    }
  }, [
    bumpHistory,
    isPersisting,
    persistBlockEncrypted,
    rowBlock,
    rowValid,
    updateDecryptedBlock,
  ]);

  const redo = useCallback(async () => {
    if (!rowBlock || !rowValid || isPersisting) return;
    const h = historyRef.current;
    if (h.future.length === 0) return;
    const restore = h.future[h.future.length - 1];
    if (restore.id !== rowBlock.id) return;
    const currentSnapshot = cloneBlock(rowBlock);
    historyRef.current = {
      past: [...h.past, currentSnapshot],
      future: h.future.slice(0, -1),
    };
    bumpHistory();
    setIsPersisting(true);
    try {
      const ok = await persistBlockEncrypted({ block: restore });
      if (!ok) {
        historyRef.current = h;
        bumpHistory();
        return;
      }
      updateDecryptedBlock({ block: restore });
    } finally {
      setIsPersisting(false);
    }
  }, [
    bumpHistory,
    isPersisting,
    persistBlockEncrypted,
    rowBlock,
    rowValid,
    updateDecryptedBlock,
  ]);

  const undoRef = useRef(undo);
  const redoRef = useRef(redo);
  undoRef.current = undo;
  redoRef.current = redo;

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (undoRedoFromEditableTarget(e.target)) return;
      const mod = e.metaKey || e.ctrlKey;
      if (!mod || e.key.toLowerCase() !== "z") return;
      e.preventDefault();
      if (e.shiftKey) void redoRef.current();
      else void undoRef.current();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const canUndoRedo = useMemo(() => {
    const { past, future } = historyRef.current;
    return {
      undo: past.length > 0,
      redo: future.length > 0,
      currentVersion: past.length + 1,
    };
  }, [historyVersion]);

  const canUndo = canUndoRedo.undo;
  const canRedo = canUndoRedo.redo;
  const currentVersion = canUndoRedo.currentVersion;

  const draftMatchesPersisted = useMemo(
    () =>
      !rowBlock || !rowValid
        ? true
        : draftDataMatchesPersisted({ rowBlock, rowValid, schema, drafts }),
    [drafts, rowBlock, rowValid, schema],
  );

  const showEditChrome =
    !isLoading && !!tableModel && rowValid && !!rowBlock;

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col items-center justify-center p-8">
      <Card className="w-full max-w-xl">
        {showEditChrome && (
          <CardHeader className="border-border flex min-h-0 flex-row flex-wrap items-center justify-between gap-3 border-b px-6 pt-6 pb-4">
            <div className="flex flex-wrap items-center gap-0">
              <Button
                type="button"
                variant="outline"
                size="icon-sm"
                className="rounded-r-none border-r-0"
                disabled={!canUndo || isPersisting}
                aria-label="Undo"
                title="Undo"
                onClick={() => void undo()}
              >
                &lt;
              </Button>
              <span
                className="border-input bg-background text-muted-foreground flex h-8 min-w-8 items-center justify-center border border-x-0 px-2 text-sm tabular-nums shadow-xs dark:border-input dark:bg-input/30"
                aria-live="polite"
                aria-label={`Version ${currentVersion}`}
              >
                {currentVersion}
              </span>
              <Button
                type="button"
                variant="outline"
                size="icon-sm"
                className="rounded-l-none border-l-0"
                disabled={!canRedo || isPersisting}
                aria-label="Redo"
                title="Redo"
                onClick={() => void redo()}
              >
                &gt;
              </Button>
            </div>
            <div
              className="flex shrink-0 items-center"
              title={
                isPersisting
                  ? "Saving…"
                  : draftMatchesPersisted
                    ? "All changes saved"
                    : "Unsaved changes"
              }
              aria-label={
                isPersisting
                  ? "Saving changes"
                  : draftMatchesPersisted
                    ? "All changes saved"
                    : "Unsaved changes"
              }
              aria-live="polite"
            >
              {isPersisting ? (
                <Loader2
                  className="text-muted-foreground size-5 animate-spin"
                  aria-hidden
                />
              ) : draftMatchesPersisted ? (
                <Check
                  className="size-5 text-emerald-600 dark:text-emerald-500"
                  aria-hidden
                />
              ) : (
                <PenLine
                  className="size-5 text-amber-600 dark:text-amber-500"
                  aria-hidden
                />
              )}
            </div>
          </CardHeader>
        )}
        <CardContent className="space-y-6 p-6">
          {isLoading ? (
            <p className="text-muted-foreground text-sm">Loading…</p>
          ) : !tableModel ? (
            <p className="text-muted-foreground text-sm">Table not found.</p>
          ) : !rowValid || !rowBlock ? (
            <p className="text-muted-foreground text-sm">
              Row not found or it does not belong to this table.
            </p>
          ) : (
            <div className="space-y-5">
              {packFormRows({ fields: editSchema }).map((row) => (
                <div
                  key={row.map((field) => field.key).join("-")}
                  className="grid grid-cols-1 gap-5 sm:grid-cols-2"
                >
                  {row.map((field) => (
                    <div
                      key={field.key}
                      className={cn(
                        "space-y-2",
                        field.formSpan !== 1 && "sm:col-span-2",
                      )}
                    >
                      <Label htmlFor={field.key}>{field.name}</Label>
                      <FieldControl
                        field={field}
                        draft={drafts[field.key] ?? { value: undefined }}
                        onChange={(next) =>
                          setDrafts((prev) => ({
                            ...prev,
                            [field.key]: next,
                          }))
                        }
                        onBlur={saveRowOnBlur}
                        persistPartial={(partial) => void saveRowDrafts(partial)}
                        onCreateOption={ensureSelectFieldOption}
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function MediaThumb({
  item,
  square,
}: {
  item: MediaItem;
  square?: boolean;
}) {
  const src = mediaItemSrc(item);
  if (mediaPreviewLooksVideo(item)) {
    return (
      <video
        src={src}
        className={
          square
            ? "bg-muted h-full w-full object-cover"
            : "bg-muted max-h-36 w-full rounded-md border object-contain"
        }
        controls={!square}
        muted={square}
        playsInline
        preload="metadata"
      />
    );
  }
  if (mediaPreviewLooksImage(item)) {
    return (
      <img
        src={src}
        alt={item.caption.trim() ? item.caption : item.value}
        className={
          square
            ? "bg-muted h-full w-full object-cover"
            : "bg-muted max-h-36 max-w-full rounded-md border object-contain"
        }
      />
    );
  }
  if (square) {
    return (
      <a
        href={src}
        className="text-primary flex h-full w-full items-center justify-center bg-muted p-1 text-center text-[10px] leading-tight break-all underline underline-offset-1"
        target="_blank"
        rel="noreferrer"
      >
        {item.value}
      </a>
    );
  }
  return (
    <a
      href={src}
      className="text-primary text-sm break-all underline underline-offset-2"
      target="_blank"
      rel="noreferrer"
    >
      {item.value}
    </a>
  );
}

function MediaListField({
  field,
  draft,
  onChange,
  onBlur,
  persistPartial,
}: {
  field: SchemaField;
  draft: FieldDraft;
  onChange: (next: FieldDraft) => void;
  onBlur?: () => void;
  persistPartial?: (partial: Record<string, FieldDraft>) => void;
}) {
  const items = useMemo(
    () => mediaItemsFromDraft(draft),
    [draft.mediaJson, draft.data],
  );
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const draftRef = useRef(draft);
  const dragFromIndex = useRef<number | null>(null);
  const [reorderInsertionGap, setReorderInsertionGap] = useState<number | null>(
    null,
  );
  const [mediaCarousel, setMediaCarousel] = useState<MediaCarouselState | null>(
    null,
  );
  const pointerPickOriginRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  const applyMediaItems = useCallback(
    (nextItems: MediaItem[]) => {
      const next = draftWithMediaItems(
        draftRef.current,
        reindexMediaItems(nextItems),
      );
      draftRef.current = next;
      onChange(next);
      persistPartial?.({ [field.key]: next });
    },
    [field.key, onChange, persistPartial],
  );

  const uploadFiles = useCallback(
    async (files: FileList | File[]) => {
      const list = Array.from(files).filter((f) => f.size > 0);
      if (list.length === 0) return;
      setUploadError(null);
      setUploading(true);
      const merged = [...mediaItemsFromDraft(draftRef.current)];
      try {
        for (const file of list) {
          const fd = new FormData();
          fd.append("file", file);
          const res = await fetch("/api/upload", { method: "POST", body: fd });
          if (!res.ok) {
            const t = await res.text();
            throw new Error(t || res.statusText);
          }
          const j = (await res.json()) as {
            url?: string;
            mime?: string;
          };
          if (!j.url) throw new Error("Upload response missing url");
          const url = j.url.trim();
          const value = url.startsWith("/uploads/")
            ? url.slice("/uploads/".length)
            : url.replace(/^\/?uploads\//, "");
          const mime =
            typeof j.mime === "string" && j.mime
              ? j.mime
              : file.type || "";
          const type: "image" | "video" = mime.startsWith("video/")
            ? "video"
            : "image";
          merged.push({
            type,
            value,
            index: merged.length,
            caption: "",
          });
        }
        applyMediaItems(merged);
      } catch (e) {
        setUploadError(e instanceof Error ? e.message : "Upload failed");
      } finally {
        setUploading(false);
      }
    },
    [applyMediaItems],
  );

  return (
    <div
      className="space-y-3"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          onBlur?.();
        }
      }}
      onPaste={(e) => {
        const { files } = e.clipboardData;
        if (files.length > 0) {
          e.preventDefault();
          void uploadFiles(files);
        }
      }}
    >
      <input
        ref={inputRef}
        type="file"
        className="sr-only"
        multiple
        accept="image/*,video/*"
        tabIndex={-1}
        aria-label={`Upload files for ${field.name}`}
        onChange={(e) => {
          const { files } = e.target;
          if (files?.length) void uploadFiles(files);
          e.target.value = "";
        }}
      />
      {uploadError ? (
        <p className="text-destructive text-sm">{uploadError}</p>
      ) : null}
      <div
        className="flex flex-wrap gap-2"
        role={items.length > 0 ? "list" : undefined}
        aria-label={items.length > 0 ? "Uploaded media" : undefined}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
            setReorderInsertionGap(null);
          }
        }}
      >
          {items.map((item, index) => {
            const insertionTouches =
              reorderInsertionGap != null &&
              (reorderInsertionGap === index ||
                reorderInsertionGap === index + 1);
            const showInsertionLeft =
              reorderInsertionGap != null && reorderInsertionGap === index;
            const showInsertionRight =
              reorderInsertionGap != null && reorderInsertionGap === index + 1;
            return (
            <div
              key={`${item.value}-${index}`}
              role="listitem"
              className={cn(
                "group border-border relative aspect-square w-24 shrink-0 overflow-hidden rounded-md border transition-[box-shadow]",
                insertionTouches &&
                  "ring-ring z-10 ring-[3px] ring-offset-2 ring-offset-background shadow-md dark:ring-offset-background",
              )}
              onDragOver={(e) => {
                e.preventDefault();
                if (dragFromIndex.current != null) {
                  e.dataTransfer.dropEffect = "move";
                  const rect = (
                    e.currentTarget as HTMLElement
                  ).getBoundingClientRect();
                  const mid = rect.left + rect.width / 2;
                  let gap =
                    e.clientX < mid ? index : Math.min(index + 1, items.length);
                  gap = Math.max(0, Math.min(gap, items.length));
                  setReorderInsertionGap(gap);
                  return;
                }
                if (e.dataTransfer.types.includes("Files")) {
                  e.dataTransfer.dropEffect = "copy";
                  setReorderInsertionGap(null);
                  return;
                }
                e.dataTransfer.dropEffect = "move";
              }}
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                  setReorderInsertionGap(null);
                }
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (e.dataTransfer.files.length > 0) {
                  void uploadFiles(e.dataTransfer.files);
                  setReorderInsertionGap(null);
                  return;
                }
                const parsed = Number.parseInt(
                  e.dataTransfer.getData("text/plain"),
                  10,
                );
                const from =
                  dragFromIndex.current ??
                  (Number.isNaN(parsed) ? -1 : parsed);
                dragFromIndex.current = null;
                setReorderInsertionGap(null);
                if (from < 0) return;
                const rect = (
                  e.currentTarget as HTMLElement
                ).getBoundingClientRect();
                const mid = rect.left + rect.width / 2;
                let insertBeforeGap =
                  e.clientX < mid ? index : Math.min(index + 1, items.length);
                insertBeforeGap = Math.max(
                  0,
                  Math.min(insertBeforeGap, items.length),
                );
                const next = moveMediaItem({
                  items,
                  from,
                  insertBeforeGap,
                });
                if (JSON.stringify(next) === JSON.stringify(items)) return;
                applyMediaItems(next);
              }}
            >
              <div
                role="button"
                tabIndex={0}
                className="absolute inset-0 cursor-grab touch-none active:cursor-grabbing"
                draggable
                aria-label={`Reorder item ${index + 1}`}
                onPointerDown={(e) => {
                  pointerPickOriginRef.current = {
                    x: e.clientX,
                    y: e.clientY,
                  };
                }}
                onClick={(e) => {
                  const o = pointerPickOriginRef.current;
                  pointerPickOriginRef.current = null;
                  if (!o) return;
                  const dx = Math.abs(e.clientX - o.x);
                  const dy = Math.abs(e.clientY - o.y);
                  if (dx > 8 || dy > 8) return;
                  e.preventDefault();
                  e.stopPropagation();
                  setMediaCarousel({
                    items,
                    initialIndex: index,
                  });
                }}
                onKeyDown={(e) => {
                  if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
                  e.preventDefault();
                  const to = e.key === "ArrowUp" ? index - 1 : index + 1;
                  if (to < 0 || to >= items.length) return;
                  const next = [...items];
                  const [removed] = next.splice(index, 1);
                  next.splice(to, 0, removed);
                  applyMediaItems(next);
                }}
                onDragStart={(e) => {
                  dragFromIndex.current = index;
                  e.dataTransfer.effectAllowed = "move";
                  e.dataTransfer.setData("text/plain", String(index));
                }}
                onDragEnd={() => {
                  dragFromIndex.current = null;
                  setReorderInsertionGap(null);
                }}
              >
                <MediaThumb item={item} square />
              </div>
              {showInsertionLeft ? (
                <div
                  aria-hidden
                  className="bg-primary pointer-events-none absolute inset-y-1 left-0 z-[6] w-1 rounded-xs shadow-[2px_0_6px_rgba(0,0,0,0.35)]"
                />
              ) : null}
              {showInsertionRight ? (
                <div
                  aria-hidden
                  className="bg-primary pointer-events-none absolute inset-y-1 right-0 z-[6] w-1 rounded-xs shadow-[-2px_0_6px_rgba(0,0,0,0.35)]"
                />
              ) : null}
              <Button
                type="button"
                variant="secondary"
                size="icon"
                className="bg-background/90 text-destructive hover:bg-destructive/10 hover:text-destructive absolute right-1 top-1 z-10 size-7 opacity-0 shadow-sm transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
                aria-label={`Remove ${item.value}`}
                onClick={(e) => {
                  e.stopPropagation();
                  const next = items.filter((_, i) => i !== index);
                  applyMediaItems(next);
                }}
              >
                <Trash2 className="size-3.5" aria-hidden />
              </Button>
            </div>
            );
          })}
        <div
          id={field.key}
          tabIndex={0}
          className={cn(
            "border-border bg-muted text-muted-foreground relative flex aspect-square w-24 shrink-0 items-center justify-center overflow-hidden rounded-md border transition-[box-shadow,background-color,border-color]",
            "focus:border-primary focus:bg-primary/10 focus:ring-ring focus:ring-[3px] focus:ring-offset-2 focus:outline-none",
            isDraggingOver && "border-primary bg-primary/10",
          )}
          aria-label={`Paste files for ${field.name}`}
          onDragEnter={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDraggingOver(true);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = "copy";
            setIsDraggingOver(true);
          }}
          onDragLeave={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
              setIsDraggingOver(false);
            }
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDraggingOver(false);
            if (e.dataTransfer.files.length > 0) {
              void uploadFiles(e.dataTransfer.files);
            }
          }}
        >
          {uploading ? (
            <Loader2 className="size-6 animate-spin" aria-hidden />
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="text-muted-foreground size-8"
              aria-label={`Upload files for ${field.name}`}
              onClick={(e) => {
                e.stopPropagation();
                inputRef.current?.click();
              }}
            >
              <Upload className="size-6" aria-hidden />
            </Button>
          )}
        </div>
      </div>
      <MediaCarouselOverlay
        open={mediaCarousel !== null}
        onOpenChange={(openNext) => {
          if (!openNext) setMediaCarousel(null);
        }}
        carousel={
          mediaCarousel ? { ...mediaCarousel, items } : null
        }
        captionEditable
        onItemsChange={({ items: next }) => applyMediaItems(next)}
      />
    </div>
  );
}

function ExpandableTextarea({
  id,
  value,
  onChange,
  onBlur,
}: {
  id: string;
  value: string;
  onChange: (next: string) => void;
  onBlur?: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Textarea
      id={id}
      value={value}
      rows={expanded ? 8 : 1}
      aria-expanded={expanded}
      className={
        expanded
          ? "field-sizing-content max-h-96 min-h-24 overflow-auto"
          : "field-sizing-fixed h-9 max-h-9 min-h-9 resize-none overflow-hidden py-1 whitespace-nowrap"
      }
      onFocus={() => setExpanded(true)}
      onBlur={() => {
        setExpanded(false);
        onBlur?.();
      }}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function FieldControl({
  field,
  draft,
  onChange,
  onBlur,
  persistPartial,
  onCreateOption,
}: {
  field: SchemaField;
  draft: FieldDraft;
  onChange: (next: FieldDraft) => void;
  onBlur?: () => void;
  persistPartial?: (partial: Record<string, FieldDraft>) => void;
  onCreateOption?: (args: {
    fieldKey: string;
    option: string;
  }) => Promise<boolean>;
}) {
  if (field.type === "checkbox") {
    const parsed = parseBoolean(draft.value);
    const sel =
      parsed === true ? "true" : parsed === false ? "false" : "unset";
    return (
      <Select
        value={sel}
        onValueChange={(x) => {
          const nextValue =
            x === "true" ? true : x === "false" ? false : undefined;
          const next = { ...draft, value: nextValue };
          onChange(next);
          persistPartial?.({ [field.key]: next });
        }}
      >
        <SelectTrigger id={field.key} className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="unset">
            <BooleanCheckboxIcon value={undefined} unsetIcon />
            <span className="sr-only">Unset</span>
          </SelectItem>
          <SelectItem value="true">
            <BooleanCheckboxIcon value={true} />
            <span className="sr-only">Yes</span>
          </SelectItem>
          <SelectItem value="false">
            <BooleanCheckboxIcon value={false} />
            <span className="sr-only">No</span>
          </SelectItem>
        </SelectContent>
      </Select>
    );
  }

  if (field.type === "date") {
    const raw = draft.value;
    const iso =
      raw != null && raw !== ""
        ? String(raw).slice(0, 10)
        : "";
    return (
      <Input
        id={field.key}
        type="date"
        value={iso}
        onBlur={onBlur}
        onChange={(e) =>
          onChange({
            ...draft,
            value: e.target.value ? e.target.value : undefined,
          })
        }
      />
    );
  }

  if (field.type === "select") {
    const applyTags = (tags: string[]) => {
      const next: FieldDraft = { ...draft, value: tags };
      onChange(next);
      persistPartial?.({ [field.key]: next });
    };
    return (
      <SelectFieldEditor
        id={field.key}
        selected={parseTagsForSelect(draft.value)}
        options={field.options ?? []}
        fieldKey={field.key}
        onSelectedChange={applyTags}
        onCreateOption={onCreateOption}
        onBlur={onBlur}
      />
    );
  }

  if (field.type === "mediaList") {
    return (
      <MediaListField
        field={field}
        draft={draft}
        onChange={onChange}
        onBlur={onBlur}
        persistPartial={persistPartial}
      />
    );
  }

  if (isMarkdownSchemaType(field.type)) {
    return (
      <MarkdownFieldEditor
        id={field.key}
        value={draft.value != null ? String(draft.value) : ""}
        onBlur={onBlur}
        onChange={(text) => onChange({ ...draft, value: text })}
      />
    );
  }

  if (isHtmlSchemaType(field.type)) {
    return (
      <HtmlFieldEditor
        id={field.key}
        collapseWhenIdle
        value={draft.value != null ? String(draft.value) : ""}
        onBlur={onBlur}
        onChange={(text) => onChange({ ...draft, value: text })}
      />
    );
  }

  if (field.cellEditor === "largeText" || isTextareaSchemaType(field.type)) {
    return (
      <ExpandableTextarea
        id={field.key}
        value={draft.value != null ? String(draft.value) : ""}
        onBlur={onBlur}
        onChange={(text) =>
          onChange({
            ...draft,
            value: text,
          })
        }
      />
    );
  }

  return (
    <Input
      id={field.key}
      type={field.type === "number" ? "number" : "text"}
      value={draft.value != null ? String(draft.value) : ""}
      onBlur={onBlur}
      onChange={(e) => {
        const t = e.target.value;
        if (field.type === "number") {
          onChange({
            ...draft,
            value: t === "" ? undefined : Number(t),
          });
        } else {
          onChange({ ...draft, value: t });
        }
      }}
    />
  );
}
